import { useState, useRef, useEffect } from 'react';
import { GlassCard } from '@/components/ui/glass-card';
import { FancySpinner } from '@/components/ui/spinner';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Bot, User, Sparkles, RefreshCw } from 'lucide-react';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  intent?: string;
  model?: string;
  timestamp: string;
}

export default function Messages() {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'welcome',
      role: 'assistant',
      content: "Hello Gabriel! I am OLU, your super human AI assistant. I'm ready to handle web research, code analysis, fact-checking, and memory persistence. How can I serve you today?",
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading]);

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || loading) return;

    const userMsg: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: trimmed,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const resp = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: trimmed }),
      });

      if (!resp.ok) throw new Error(`HTTP error ${resp.status}`);

      const data = await resp.json<{ response?: string; intent?: string; model?: string }>();

      const assistantMsg: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: data.response || "Something glitched on my end — try again in a moment.",
        intent: data.intent,
        model: data.model,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      };

      setMessages((prev) => [...prev, assistantMsg]);
    } catch (e) {
      const errorMsg: ChatMessage = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: "Unable to reach the OLU chat engine. Please verify network configuration.",
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto h-[calc(100vh-6rem)] flex flex-col space-y-4">
      <div className="flex items-center justify-between border-b border-white/10 pb-4">
        <div>
          <h1 className="text-2xl font-extrabold text-white flex items-center gap-2.5">
            <Bot className="text-cyan-400" size={24} />
            OLU Live Chat
          </h1>
          <p className="text-xs text-slate-400">Agentic Web Search & Memory Persistence Engine</p>
        </div>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => setMessages([messages[0]])}
          className="border-white/10 text-slate-300 hover:bg-white/5 rounded-xl gap-2"
        >
          <RefreshCw size={14} />
          Clear Chat
        </Button>
      </div>

      <GlassCard glow="cyan" className="flex-1 p-6 flex flex-col justify-between overflow-hidden relative">
        <div className="flex-1 overflow-y-auto space-y-6 pr-2 custom-scrollbar">
          <AnimatePresence initial={false}>
            {messages.map((msg) => (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className={`flex gap-4 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {msg.role === 'assistant' && (
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-cyan-500 to-blue-600 flex items-center justify-center text-white shrink-0 shadow-[0_0_15px_rgba(6,182,212,0.4)]">
                    <Sparkles size={18} />
                  </div>
                )}

                <div className={`max-w-2xl space-y-2 ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                  <div
                    className={`p-4 rounded-2xl text-sm leading-relaxed ${
                      msg.role === 'user'
                        ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-tr-none shadow-lg shadow-cyan-500/20 font-medium'
                        : 'bg-slate-900/90 text-slate-200 border border-white/10 rounded-tl-none backdrop-blur-md shadow-lg'
                    }`}
                  >
                    <p className="whitespace-pre-wrap">{msg.content}</p>
                  </div>

                  <div className="flex items-center gap-2 px-1 text-[11px] text-slate-500">
                    <span>{msg.timestamp}</span>
                    {msg.intent && (
                      <span className="px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 font-mono text-[10px]">
                        Intent: {msg.intent}
                      </span>
                    )}
                    {msg.model && (
                      <span className="px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-400 border border-purple-500/20 font-mono text-[10px]">
                        {msg.model}
                      </span>
                    )}
                  </div>
                </div>

                {msg.role === 'user' && (
                  <div className="w-9 h-9 rounded-xl bg-slate-800 border border-white/10 flex items-center justify-center text-slate-300 shrink-0">
                    <User size={18} />
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>

          {loading && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-3 p-4 rounded-2xl bg-slate-900/60 border border-white/10 w-fit">
              <FancySpinner size="sm" variant="gradient" />
              <span className="text-xs font-mono text-cyan-400 animate-pulse">OLU is reasoning & researching...</span>
            </motion.div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <div className="pt-4 border-t border-white/10 mt-4">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSend();
            }}
            className="flex items-center gap-3"
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask OLU anything or tell OLU facts to remember..."
              disabled={loading}
              className="flex-1 bg-slate-950/80 border border-white/15 rounded-xl px-5 py-4 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/50 transition-all"
            />
            <Button
              type="submit"
              disabled={!input.trim() || loading}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold h-13 px-6 rounded-xl shadow-lg shadow-cyan-500/25 transition-all hover:scale-105 disabled:opacity-50 disabled:hover:scale-100"
            >
              <Send size={18} />
            </Button>
          </form>
        </div>
      </GlassCard>
    </div>
  );
}
